import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  TextFieldProps,
  Theme,
  Box,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import { SxProps } from '@mui/system';
import { theme } from 'common/theme/theme';
import { Styles } from 'common/types/styles';
import { ChangeEvent, MouseEvent } from 'react';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

export interface SelectFieldOption {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
}

export interface LazyLoadingConfig {
  hasMore: boolean;
  onLoadMore: () => void;
  loadingMore?: boolean;
}

const cssBlur = 'blur(5px)';

const styles: Styles = {
  select: {
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  optionText: {
    fontSize: '12px',
    maxWidth: '400px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  selectedOptionText: {
    fontSize: '12px',
    fontWeight: theme.typography.fontWeightMedium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '400px',
    whiteSpace: 'nowrap',
  },
  optionIcon: {
    marginRight: theme.spacing(1),
    minWidth: 'unset !important',
  },
  selectedOptionIcon: {
    minWidth: 'unset !important',
    marginRight: theme.spacing(1),
    color: '#000',
  },
  listItemText: {
    flex: 'unset',
    minWidth: 0, // Allow text to shrink
    '& .MuiListItemText-primary': {
      maxWidth: '400px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  loadMoreButton: {
    padding: theme.spacing(1, 2),
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: 'background.paper',
    transition: 'all 0.2s ease-in-out',
    minHeight: 'auto',
    height: 'auto',
    '&:hover': {
      backgroundColor: 'action.hover',
      '& .MuiTypography-root': {
        color: 'primary.dark',
      },
      '& .MuiSvgIcon-root': {
        color: 'primary.dark',
        transform: 'translateY(1px)',
      },
    },
    '&:active': {
      backgroundColor: 'action.selected',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
      '&:hover': {
        backgroundColor: 'background.paper',
        '& .MuiTypography-root': {
          color: 'text.secondary',
        },
        '& .MuiSvgIcon-root': {
          transform: 'none',
        },
      },
    },
  },
};

interface Props<T> {
  options: SelectFieldOption[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: T;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: T) => void;
  error?: string;
  multiple?: boolean;
  sx?: SxProps<Theme>;
  selectSx?: SxProps<Theme>;
  label?: string;
  disabled?: boolean;
  variant?: TextFieldProps['variant'];
  placeholder?: string;
  fullWidth?: boolean;
  demoBlur?: boolean;
  loading?: boolean;
  lazyLoading?: LazyLoadingConfig;
  size?: TextFieldProps['size'];
  SelectProps?: TextFieldProps['SelectProps'];
}

export function SelectField<T = unknown>({
  options,
  value,
  onChange,
  sx,
  selectSx,
  error,
  multiple = false,
  label,
  disabled = false,
  variant,
  fullWidth,
  demoBlur = false,
  placeholder,
  loading = false,
  lazyLoading,
  size = 'small',
  SelectProps,
}: Props<T>) {
  const handleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value as string;
    // Ignore the special load-more value
    if (newValue === '__load_more__') {
      return;
    }
    onChange(newValue as never);
  };

  const handleIsOptionSelected = (optionValue: string | number) => {
    return Array.isArray(value) ? value.some(item => item === optionValue) : value === optionValue;
  };

  const handleLoadMore = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (lazyLoading?.onLoadMore && !lazyLoading?.loadingMore) {
      lazyLoading.onLoadMore();
    }
  };

  return (
    <TextField
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value={value as any}
      SelectProps={{
        multiple: Array.isArray(value) && multiple,
        sx: selectSx,
        MenuProps: {
          PaperProps: {
            sx: {
              maxHeight: 300,
              overflowY: 'auto',
            },
          },
        },
        ...SelectProps,
      }}
      onChange={handleValueChange}
      error={Boolean(error)}
      size={size}
      sx={[styles.select, ...(Array.isArray(sx) ? sx : [sx])]}
      helperText={error}
      label={label}
      disabled={disabled || loading}
      variant={variant}
      fullWidth={fullWidth}
      placeholder={placeholder}
      select
    >
      {loading && options.length === 0 ? (
        <MenuItem disabled>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" sx={styles.optionText}>
              Loading...
            </Typography>
          </Box>
        </MenuItem>
      ) : (
        [
          ...options.map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{ ...styles.selectedOptionText, ...(demoBlur && { filter: cssBlur }) }}
            >
              {option.icon && (
                <ListItemIcon sx={handleIsOptionSelected(option.value) ? styles.selectedOptionIcon : styles.optionIcon}>
                  {option.icon}
                </ListItemIcon>
              )}
              <ListItemText
                sx={styles.listItemText}
                primary={option.label}
                primaryTypographyProps={{
                  sx: handleIsOptionSelected(option.value) ? styles.selectedOptionText : styles.optionText,
                }}
              />
            </MenuItem>
          )),

          ...(lazyLoading?.hasMore
            ? [
                <Divider key="divider" sx={{ my: 0 }} />,
                <MenuItem
                  key="load-more"
                  value="__load_more__"
                  onMouseDown={handleLoadMore}
                  sx={styles.loadMoreButton}
                  disabled={lazyLoading.loadingMore}
                  disableRipple
                >
                  {lazyLoading.loadingMore ? (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} thickness={4} />
                      <Typography variant="body2" sx={{ ...styles.optionText, fontWeight: 500 }}>
                        Loading more...
                      </Typography>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <ExpandMoreIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2" sx={{ ...styles.optionText, fontWeight: 500, color: 'primary.main' }}>
                        Load more options
                      </Typography>
                    </Box>
                  )}
                </MenuItem>,
              ]
            : []),

          ...(options.length === 0 && !loading
            ? [
                <MenuItem key="no-options" disabled>
                  <Typography variant="body2" sx={styles.optionText}>
                    No options available
                  </Typography>
                </MenuItem>,
              ]
            : []),
        ]
      )}
    </TextField>
  );
}
